import api from './api';
import {
  SISConfiguration,
  CustomIndicator,
  IndicatorValue,
  SISSyncLog,
  SISDataMapping,
  SISWebhookEvent,
  IntegrationStatus,
  SyncSchoolRequest,
  CreateIndicatorRequest,
  SISDashboardStats,
  IndicatorChartData
} from '../types/sis';

// Base SIS Integration API endpoints
const SIS_BASE = '/sis-integration';

// SIS Configuration endpoints
export const sisConfigurationApi = {
  getAll: () => api.get(`${SIS_BASE}/configurations/`),
  getById: (id: number) => api.get(`${SIS_BASE}/configurations/${id}/`),
  create: (data: SISConfiguration) => api.post(`${SIS_BASE}/configurations/`, data),
  update: (id: number, data: Partial<SISConfiguration>) => 
    api.patch(`${SIS_BASE}/configurations/${id}/`, data),
  delete: (id: number) => api.delete(`${SIS_BASE}/configurations/${id}/`),
  
  // Sync operations
  fullSync: (id: number) => api.post(`${SIS_BASE}/configurations/${id}/full_sync/`),
  syncSchool: (id: number, data: SyncSchoolRequest) => 
    api.post(`${SIS_BASE}/configurations/${id}/sync_school/`, data),
  testConnection: (id: number) => api.post(`${SIS_BASE}/configurations/${id}/test_connection/`),
};

// Custom Indicators endpoints
export const customIndicatorApi = {
  getAll: (params?: { 
    category?: string; 
    is_active?: boolean; 
    search?: string;
    page?: number;
    page_size?: number;
  }) => api.get(`${SIS_BASE}/indicators/`, { params }),
  
  getById: (id: number) => api.get(`${SIS_BASE}/indicators/${id}/`),
  create: (data: CreateIndicatorRequest) => api.post(`${SIS_BASE}/indicators/create/`, data),
  update: (id: number, data: Partial<CustomIndicator>) => 
    api.patch(`${SIS_BASE}/indicators/${id}/`, data),
  delete: (id: number) => api.delete(`${SIS_BASE}/indicators/${id}/`),
  
  // Calculate indicator for specific school
  calculate: (id: number, schoolId: number) => 
    api.post(`${SIS_BASE}/indicators/${id}/calculate/`, { school_id: schoolId }),
  
  // Get indicator statistics
  getStats: (id: number, params?: {
    academic_year?: string;
    period?: string;
    region_id?: number;
    district_id?: number;
  }) => api.get(`${SIS_BASE}/indicators/${id}/stats/`, { params }),
};

// Indicator Values endpoints
export const indicatorValueApi = {
  getAll: (params?: {
    indicator?: number;
    school?: number;
    academic_year?: string;
    period?: string;
    source?: string;
    page?: number;
    page_size?: number;
  }) => api.get(`${SIS_BASE}/indicator-values/`, { params }),
  
  getById: (id: number) => api.get(`${SIS_BASE}/indicator-values/${id}/`),
  create: (data: Partial<IndicatorValue>) => api.post(`${SIS_BASE}/indicator-values/`, data),
  update: (id: number, data: Partial<IndicatorValue>) => 
    api.patch(`${SIS_BASE}/indicator-values/${id}/`, data),
  delete: (id: number) => api.delete(`${SIS_BASE}/indicator-values/${id}/`),
  
  // Bulk operations
  bulkCreate: (data: Partial<IndicatorValue>[]) => 
    api.post(`${SIS_BASE}/indicator-values/bulk_create/`, data),
  bulkUpdate: (data: { id: number; values: Partial<IndicatorValue> }[]) => 
    api.patch(`${SIS_BASE}/indicator-values/bulk_update/`, data),
};

// Sync Logs endpoints
export const syncLogApi = {
  getAll: (params?: {
    sync_type?: string;
    status?: string;
    entity_type?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }) => api.get(`${SIS_BASE}/sync-logs/`, { params }),
  
  getById: (id: number) => api.get(`${SIS_BASE}/sync-logs/${id}/`),
  getLatest: () => api.get(`${SIS_BASE}/sync-logs/latest/`),
  retry: (id: number) => api.post(`${SIS_BASE}/sync-logs/${id}/retry/`),
};

// Data Mapping endpoints
export const dataMappingApi = {
  getAll: (params?: {
    entity_type?: string;
    is_active?: boolean;
    sis_config?: number;
  }) => api.get(`${SIS_BASE}/data-mappings/`, { params }),
  
  getById: (id: number) => api.get(`${SIS_BASE}/data-mappings/${id}/`),
  create: (data: Partial<SISDataMapping>) => api.post(`${SIS_BASE}/data-mappings/`, data),
  update: (id: number, data: Partial<SISDataMapping>) => 
    api.patch(`${SIS_BASE}/data-mappings/${id}/`, data),
  delete: (id: number) => api.delete(`${SIS_BASE}/data-mappings/${id}/`),
  
  // Test mapping
  testMapping: (id: number, sampleData: any) => 
    api.post(`${SIS_BASE}/data-mappings/${id}/test/`, { sample_data: sampleData }),
};

// Webhook Events endpoints
export const webhookEventApi = {
  getAll: (params?: {
    event_type?: string;
    entity_type?: string;
    processed?: boolean;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }) => api.get(`${SIS_BASE}/webhook-events/`, { params }),
  
  getById: (id: number) => api.get(`${SIS_BASE}/webhook-events/${id}/`),
  reprocess: (id: number) => api.post(`${SIS_BASE}/webhook-events/${id}/reprocess/`),
  markProcessed: (id: number) => api.patch(`${SIS_BASE}/webhook-events/${id}/`, { processed: true }),
};

// Integration Status and Dashboard
export const integrationApi = {
  getStatus: () => api.get<IntegrationStatus>(`${SIS_BASE}/status/`),
  getDashboardStats: () => api.get<SISDashboardStats>(`${SIS_BASE}/dashboard-stats/`),
  
  // Real-time sync
  startRealTimeSync: (configId: number) => 
    api.post(`${SIS_BASE}/real-time/start/`, { config_id: configId }),
  stopRealTimeSync: (configId: number) => 
    api.post(`${SIS_BASE}/real-time/stop/`, { config_id: configId }),
  
  // Data visualization
  getIndicatorChart: (indicatorId: number, params?: {
    academic_year?: string;
    period?: string;
    group_by?: 'school' | 'district' | 'region';
    chart_type?: 'bar' | 'line' | 'pie';
  }) => api.get<IndicatorChartData>(`${SIS_BASE}/indicators/${indicatorId}/chart/`, { params }),
  
  getComparisonChart: (indicatorIds: number[], params?: {
    academic_year?: string;
    period?: string;
    schools?: number[];
  }) => api.get(`${SIS_BASE}/charts/comparison/`, { 
    params: { indicator_ids: indicatorIds.join(','), ...params } 
  }),
  
  getTrendChart: (indicatorId: number, params?: {
    years?: string[];
    school_id?: number;
    district_id?: number;
  }) => api.get(`${SIS_BASE}/indicators/${indicatorId}/trend/`, { params }),
};

// Webhook endpoint for receiving SIS events
export const webhookApi = {
  receive: (data: any) => api.post(`${SIS_BASE}/webhook/`, data),
};

// Utility functions
export const sisUtils = {
  // Validate indicator code format
  validateIndicatorCode: (code: string): boolean => {
    return /^[A-Z_][A-Z0-9_]*$/.test(code);
  },
  
  // Format indicator value based on data type
  formatIndicatorValue: (value: any, dataType: CustomIndicator['data_type']): string => {
    if (value === null || value === undefined) return 'N/A';
    
    switch (dataType) {
      case 'integer':
        return Number(value).toLocaleString();
      case 'decimal':
        return Number(value).toLocaleString(undefined, { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        });
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'json':
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
      case 'text':
      default:
        return String(value);
    }
  },
  
  // Get indicator categories for filtering
  getIndicatorCategories: () => [
    { value: 'enrollment', label: 'Enrollment' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'academic', label: 'Academic Performance' },
    { value: 'staff', label: 'Staff Information' },
    { value: 'finance', label: 'Financial' },
    { value: 'custom', label: 'Custom Metric' },
  ],
  
  // Get data types for indicator creation
  getDataTypes: () => [
    { value: 'integer', label: 'Integer' },
    { value: 'decimal', label: 'Decimal' },
    { value: 'text', label: 'Text' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'date', label: 'Date' },
    { value: 'json', label: 'JSON Object' },
  ],
  
  // Get sync frequencies
  getSyncFrequencies: () => [
    { value: 'real_time', label: 'Real Time' },
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
  ],
  
  // Get auth types
  getAuthTypes: () => [
    { value: 'api_key', label: 'API Key' },
    { value: 'basic', label: 'Basic Auth' },
    { value: 'oauth', label: 'OAuth' },
    { value: 'token', label: 'Token Based' },
  ],
};

export default {
  sisConfigurationApi,
  customIndicatorApi,
  indicatorValueApi,
  syncLogApi,
  dataMappingApi,
  webhookEventApi,
  integrationApi,
  webhookApi,
  sisUtils,
};